import React, { useEffect, useState } from "react";
import { Form, Button, Modal, Popconfirm } from "antd";
import { ReactSVG } from "react-svg";
import { useSelector } from "react-redux";
import { omit } from "lodash";

import ListContainer from "../../modules/driver/containers/List";
import DriversTable from "../Table/Drivers";
import DriverForm from "../Forms/Drivers/Driver";
import DriversFilterForm from "../Forms/Filters/Drivers";
import withPage from "./withPage";
import RoutePlanCurrent from "../RoutePlan/RoutePlanCurrent";
import PrimaryButton from "../Buttons/PrimaryButton";

import { getTranslation } from "../../utils/helper";
import Cookies from "js-cookie";

const PAGE_SIZE = 100;
const defaultCountryCode = window.location.href.includes("nordicrouting.se") ? "se" : "sg";
const DriversPage = ({
  isFetching,
  status,
  drivers,
  metadata,
  query,
  pushTo,
  doGetAllDrivers,
  doCreateDriver,
  doUpdateDriver,
  doDeleteDriver,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [countryCodeInit, setCountryCodeInit] = useState(defaultCountryCode);

  const user = useSelector(state => state.auth.user);
  const setting = user.company;

  const [form] = Form.useForm();
  const currentPage = Number(query.currentPage) || 1;
  const itemsPerPage = Number(query.itemsPerPage) || PAGE_SIZE;
  const pagination = {
    current: currentPage,
    pageSize: itemsPerPage,
    total: metadata.totalItems,
    showTotal: total => `${total} ${getTranslation(setting, "Drivers", "Drivers")}`,
  };
  const [state, setState] = useState({
    visibleModal: false,
    action: "",
    driver: null,
  });

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      setCountryCodeInit(Cookies.get(`CountryCode-${user._id}`) || defaultCountryCode);
    }
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (!isFetching && status === "succeeded" && isMounted) {
      handleCancel();
    }
    return () => {
      isMounted = false;
    };
  }, [isFetching]);

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      doGetAllDrivers({ currentPage, itemsPerPage, ...query });
    }
    return () => {
      isMounted = false;
    };
  }, []);

  const handleFilter = (params, updatePath) => {
    if (updatePath) {
      pushTo({ query: params });
    }
    const currentPage = Number(params.currentPage) || 1;
    const itemsPerPage = Number(params.itemsPerPage) || PAGE_SIZE;
    doGetAllDrivers({
      currentPage,
      itemsPerPage,
      ...params,
    });
  };

  const handleTableChange = params => {
    handleFilter(params, true);
  };

  const withAction = item => {
    return (
      <div className="actions">
        <Button
          type="link"
          className="edit"
          onClick={() => {
            handleClickAction("edit", item);
          }}>
          <ReactSVG className="icon" src="/icons/edit.svg" />
        </Button>
        <Button
          type="link"
          className="duplicate"
          onClick={() => {
            handleClickAction("duplicate", item);
          }}>
          <ReactSVG className="icon" src="/icons/duplicate.svg" />
        </Button>
        <Popconfirm
          title={getTranslation(setting, "Common", "Are you sure to delete this item?")}
          onConfirm={() => handleClickDelete(item)}
          okText={getTranslation(setting, "Common", "Yes")}
          cancelText={getTranslation(setting, "Common", "No")}>
          <Button type="link" className="delete">
            <ReactSVG className="icon" src="/icons/delete.svg" />
          </Button>
        </Popconfirm>
      </div>
    );
  };

  const handleClickDelete = async item => {
    await doDeleteDriver(item._id);
    await doGetAllDrivers({ currentPage, itemsPerPage, ...query });
  };

  // const speeds = {
  //   X2SLOWER: 0,
  //   SLOWER: 15,
  //   SLOW: 30,
  //   NORMAL: 50,
  //   FAST: 70,
  //   FASTER: 85,
  //   X2FASTER: 100,
  // };

  const speeds = {
    X2SLOWER: 30,
    SLOWER: 30,
    SLOW: 30,
    NORMAL: 50,
    FAST: 70,
    FASTER: 70,
    X2FASTER: 70,
  };

  const handleClickAction = (action, item) => {
    form.resetFields();
    form.setFieldsValue({
      ...(action === "duplicate" ? omit(item, "name", "email", "phone") : item),
      startLocation: {
        address: item?.startAddress,
        latLng: { lat: item?.startLat, lng: item?.startLng },
      },
      endLocation: {
        address: item?.endAddress,
        latLng: { lat: item?.endLat, lng: item?.endLng },
      },
      shiftStart: item?.shiftStart,
      shiftEnd: item?.shiftEnd,
      speed: speeds[item?.speed] ?? 50, // 50: Normal
    });
    setState({
      ...state,
      action,
      driver: action === "duplicate" ? null : item,
      visibleModal: true,
    });
  };

  const handleSubmit = async values => {
    const params = setParams(values);
    if (state.driver) {
      await doUpdateDriver({ id: state.driver._id, ...params });
    } else {
      await doCreateDriver(params);
      await doGetAllDrivers({ currentPage, itemsPerPage, ...query });
    }
  };

  const handleCancel = () => {
    setState({ ...state, visibleModal: false });
    form.resetFields();
  };

  const setParams = values => {
    const params = { ...values };
    params.startAddress = values.startLocation?.address;
    params.startLat = values.startLocation?.latLng?.lat;
    params.startLng = values.startLocation?.latLng?.lng;
    params.endAddress = values.endLocation?.address;
    params.endLat = values.endLocation?.latLng?.lat;
    params.endLng = values.endLocation?.latLng?.lng;
    params.shiftStart = values.shiftStart;
    params.shiftEnd = values.shiftEnd;
    params.email = values.email || null;
    params.skills = values.skills || [];
    return params;
  };
  return (
    <div className="Content DriversPage">
      <RoutePlanCurrent />
      <div className="ContentHeader">
        <div className="HeaderTitle">{getTranslation(setting, "Drivers", "Drivers")}</div>
        <div className="HeaderToolBar">
          <div className="ToolBarButtons">
            <Button
              className={`FilterButton ${showFilters ? "Open" : "Close"}`}
              type="primary"
              onClick={() => setShowFilters(!showFilters)}>
              {getTranslation(setting, "Common", "Filter")}
              <ReactSVG className="icon" src={`/icons/filter${showFilters ? "-close" : ""}.svg`} />
            </Button>
            <PrimaryButton
              label={getTranslation(setting, "Drivers", "New Driver")}
              className="NewButton"
              icon={<ReactSVG className="icon" src="/icons/add.svg" />}
              onClick={() => {
                handleClickAction("new", null);
              }}
            />
          </div>
        </div>
      </div>
      <div className="ContentFilters">
        {showFilters && <DriversFilterForm isFetching={isFetching} query={query} onSubmit={handleFilter} />}
      </div>
      <div className="ContentMain">
        <DriversTable
          rowKey="_id"
          loading={isFetching}
          query={query}
          withAction={withAction}
          pagination={pagination}
          dataSource={drivers}
          onChange2={handleTableChange}
          scrollHeight="calc(100vh - 285px)"
          actionWidth={150}
          setting={setting}
        />
      </div>

      <Modal open={state.visibleModal} onCancel={handleCancel} forceRender footer={[]} width={792}>
        <DriverForm
          form={form}
          isFetching={isFetching}
          driver={state.driver}
          countryCodeInit={countryCodeInit}
          handleSubmit={handleSubmit}
          handleCancel={handleCancel}
        />
      </Modal>
    </div>
  );
};

export default withPage(ListContainer(DriversPage));
